import styled, { x } from '@xstyled/styled-components';
import React, { MouseEventHandler, useEffect, useRef, useState } from 'react';
import { t } from 'ttag';
import { select } from '~/lib/select';
import { Button } from '~/ui/buttons/Button';
import { Flex } from '~/ui/flex/Flex';
import { LlamaModal } from '~/ui/llama-modal/LlamaModal';
import { TrainingState, useTrainingStore } from '../state';
import { OnboardingStages, useOnboardingStore } from './Store';
import { useNextSequence } from '~/training/useNextSequence';
import { LargeLocaleSelector } from '~/ui/locale-selector/large-locale-selector/LargeLocaleSelector';
import { Root } from '~/training/settings/Root';
import { MediumLocalePage } from '~/training/settings/locales/MediumLocalePage';
import { TextLocalePage } from '~/training/settings/locales/TextLocalePage';
import { SubtitleLocalePage } from '~/training/settings/locales/SubtitleLocalePage';
import { DarkModePage } from '~/training/settings/locales/DarkModePage';
import { PlaybackSpeed } from '~/training/settings/playback-speed/PlaybackSpeed';
import { Overlay } from '~/ui/overlay/Overlay';
import { useSettingsStore } from '~/training/settings/Store';
import { withTransition } from '~/ui/transition/Transition';
import { Card } from '~/ui/card/Card';
import { Logo } from '~/ui/logo/Logo';

const LanguageSelectionWrapper = styled.div`
  .modalStyleForDarkTheme {
    height: fit-content;
  }
`;

const TrainingTitle = styled.div`
  font-size: 14px;
  text-align: center;
  font-weight: 700;
  color: gray;
`;

const SettingsButton = styled.div`
  font-weight: 700;
  font-size: 14px;
  text-align: center;
  color: accentDark;
  cursor: pointer;
  margin-top: 12px;
`;

const SectionLogo = styled(Logo)`
  height: 40px;
  width: auto;
  margin: 0;
`;

const TitleBlock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-direction: column;
`;

const selector = (s: TrainingState) =>
  select(
    s,
    'textLocales',
    'languages',
    'onboarding',
    'shouldDisplayCourseDurationCard',
    'disclaimer',
    'logoUrl',
    'title',
    'scorePassingPercent'
  );

const SettingsCard = withTransition(Card);

export const LanguageSelection = ({
  isOnboarding
}: {
  isOnboarding?: boolean;
}) => {
  const buttonRef = useRef(null);
  const [buttonBackground, setButtonBackground] = useState('');
  const { stage, next, goTo } = useOnboardingStore();
  const {
    textLocales,
    onboarding,
    shouldDisplayCourseDurationCard,
    disclaimer,
    logoUrl,
    title,
    scorePassingPercent
  } = useTrainingStore(selector);
  const { open, toggle } = useSettingsStore();
  const nextSequence = useNextSequence();

  useEffect(() => {
    if (buttonRef.current) {
      const computedStyle = getComputedStyle(buttonRef.current);
      const bgColor = computedStyle.backgroundColor;
      const result = bgColor
        .match(/\d+/g)
        ?.map(num => parseInt(num, 10).toString(16).padStart(2, '0'))
        .join('');

      if (result) setButtonBackground(result);
    }
  }, []);

  const GoNext = () => {
    if (onboarding) {
      return next();
    } else {
      if (shouldDisplayCourseDurationCard) {
        return goTo(OnboardingStages.CourseDuration);
      } else if (disclaimer) {
        return goTo(OnboardingStages.Disclaimer);
      } else {
        if (scorePassingPercent && scorePassingPercent > 0) {
          return goTo(OnboardingStages.PassFail);
        } else {
          nextSequence().then();
        }
      }
    }
  };

  const defaultLng = ['en'];

  if (textLocales.includes('es')) {
    defaultLng.push('es');
  }

  const locales = [
    ...defaultLng,
    ...textLocales.filter(i => i != 'en' && i != 'es')
  ];

  return (
    <LanguageSelectionWrapper>
      {stage === OnboardingStages.LanguageSelection && (
        <LlamaModal horizontal="center" vertical="center">
          <Flex
            data-test-id="language-selection-modal"
            minHeight="md"
            w="100%"
            minWidth={{ _: '100%', sm: 'lg' }}
            flexDirection="column"
            p="xl"
            alignItems="center"
            justifyContent="space-between"
            gap="22px"
          >
            <SectionLogo src={logoUrl} />

            <TitleBlock>
              <x.h2 fontFamily="Recoleta">{t`Welcome!`}</x.h2>

              <TrainingTitle>{title}</TrainingTitle>

              <small>{t`Please select your language:`}</small>
            </TitleBlock>

            <LargeLocaleSelector
              locales={locales}
              reloadSubtitle={true}
              isOnboarding={isOnboarding}
            />

            <div>
              <Button
                ref={buttonRef}
                backgroundColor={buttonBackground}
                onClick={GoNext}
                disabled={stage !== OnboardingStages.LanguageSelection}
                aria-hidden={stage !== OnboardingStages.LanguageSelection}
                padding="11px 56px"
                data-test-id="continue-button"
              >
                {t`Continue`}
              </Button>
              <SettingsButton
                onClick={toggle as unknown as MouseEventHandler<HTMLDivElement>}
              >
                {t`Accessibility Settings`}
              </SettingsButton>
            </div>
          </Flex>
        </LlamaModal>
      )}

      <Overlay open={open} opacity={0.1}>
        <Flex
          zIndex="dialogs"
          p={{ _: 'sm', sm: 'xl' }}
          minHeight="100%"
          justifyContent={{ _: 'center', sm: 'center' }}
          alignItems={{ _: 'center', sm: 'center' }}
          overflow="hidden"
          aria-modal={open}
        >
          <SettingsCard show={open} w={{ _: '100%', sm: 'auto' }}>
            <Root headerTitle={t`Accessibility Settings`} />
            <MediumLocalePage />
            <TextLocalePage />
            <SubtitleLocalePage />
            <DarkModePage />
            <PlaybackSpeed />
          </SettingsCard>
        </Flex>
      </Overlay>
    </LanguageSelectionWrapper>
  );
};
